import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Typography,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Modal,
  message,
  Spin,
  Row,
  Col,
  Form,
  Descriptions,
  Tabs,
  Timeline,
  Alert,
  Input as AntInput,
} from "antd";
import {
  EyeOutlined,
  MailOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeInvisibleOutlined,
  LinkOutlined,
  WarningOutlined,
  BarChartOutlined,
  SendOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { AdminNavBar } from "../components/AdminNavBar";
import { trackFeatureUsage } from "../lib/posthog";
import dayjs, { Dayjs } from "dayjs";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";

const { TabPane } = Tabs;

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

interface EmailNotification {
  id: number;
  booking_id: number | null;
  container_id: number | null;
  notification_type: string;
  recipient: string;
  status: string | null;
  sent_at: string | null;
  created_at: string;
  email_subject: string | null;
  email_body_html: string | null;
  email_body_text: string | null;
  mailgun_message_id: string | null;
  error_message: string | null;
  booking_reference: string | null;
  company_name: string | null;
  // For status_change notifications
  old_status: string | null;
  new_status: string | null;
  // For ETA-related notifications (expected delivery / operational)
  old_eta: string | null;
  new_eta: string | null;
  container_reference: string | null;
  transport_type: string | null;
}

interface MailgunEvent {
  event: string;
  timestamp: number;
  message?: {
    headers: {
      'message-id': string;
      'to': string;
      'from': string;
      'subject': string;
    };
  };
  recipient?: string;
  delivery?: {
    smtp_response: string;
    message: string;
    code: number;
  };
  'delivery-status'?: {
    message: string;
    code: number;
    description: string;
  };
  'client-info'?: {
    'client-name': string;
    'client-os': string;
    'device-type': string;
    'client-type': string;
    'user-agent': string;
  };
  'geolocation'?: {
    country: string;
    region: string;
    city: string;
  };
  ip?: string;
  'url'?: string;
  reason?: string;
  'tags'?: string[];
  envelope?: {
    sender?: string;
    targets?: string;
    transport?: string;
  };
}

interface MailgunEventsData {
  success: boolean;
  messageId: string;
  summary: {
    total: number;
    eventTypes: string[];
    opens: number;
    clicks: number;
    delivered: number;
    failed: number;
    bounced: number;
    complained: number;
    unsubscribed: number;
    firstOpen: number | null;
    lastOpen: number | null;
    firstClick: number | null;
    lastClick: number | null;
  };
  events: MailgunEvent[];
  eventsByType: Record<string, MailgunEvent[]>;
}

const EmailNotificationsAdmin: React.FC = () => {
  const [notifications, setNotifications] = useState<EmailNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<EmailNotification | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [mailgunEvents, setMailgunEvents] = useState<MailgunEventsData | null>(null);
  const [mailgunLoading, setMailgunLoading] = useState(false);
  const [metrics, setMetrics] = useState({
    total: 0,
    sent: 0,
    pending: 0,
    failed: 0,
    totalOpens: 0,
    totalClicks: 0,
    totalBounced: 0,
    totalComplained: 0,
    openRate: 0,
    clickRate: 0,
    bounceRate: 0,
    complaintRate: 0,
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [filters, setFilters] = useState({
    bookingId: undefined as number | undefined,
    recipient: undefined as string | undefined,
    templateId: undefined as string | undefined,
    status: undefined as string | undefined,
    dateRange: undefined as [Dayjs, Dayjs] | undefined,
  });
  const [testForm] = Form.useForm();
  const [selectedTestStatus, setSelectedTestStatus] = useState<string>('');
  const [testStatusMessagePreview, setTestStatusMessagePreview] = useState<string>('');
  const [activeMainTab, setActiveMainTab] = useState<string>('notifications');
  const [statusMessages, setStatusMessages] = useState<Array<{ status: string; message_html: string; id?: number }>>([]);
  const [statusMessagesLoading, setStatusMessagesLoading] = useState(false);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [statusMessageForm] = Form.useForm();

  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    trackFeatureUsage("email_notifications_admin");
    fetchNotifications();
    fetchMetrics();
    setupRealtimeSubscription();

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  const fetchMetrics = async () => {
    try {
      setMetricsLoading(true);
      
      // IMPORTANT: These metrics ONLY include emails from the new system (booking_email_notifications table)
      // n8n emails are stored in email_notifications table and are NOT included here
      // This ensures we only track emails sent via the new Supabase Edge Function system
      
      // Fetch basic counts from database (ONLY from new system table)
      const { data: statusCounts, error: statusError } = await supabase
        .from("booking_email_notifications")
        .select("status")
        .not("status", "is", null);

      if (statusError) throw statusError;

      // Count by status
      const counts = {
        total: statusCounts?.length || 0,
        sent: statusCounts?.filter((n) => n.status === "sent").length || 0,
        pending: statusCounts?.filter((n) => n.status === "pending").length || 0,
        failed: statusCounts?.filter((n) => n.status === "failed").length || 0,
      };

      // Fetch all sent notifications with mailgun_message_id to get event stats
      // NOTE: Only emails from booking_email_notifications (new system), NOT n8n emails
      const { data: sentNotifications, error: sentError } = await supabase
        .from("booking_email_notifications")
        .select("mailgun_message_id")
        .eq("status", "sent")
        .not("mailgun_message_id", "is", null)
        .limit(1000); // Limit to avoid too many API calls

      if (sentError) throw sentError;

      // Fetch Mailgun events for a sample of sent emails to estimate opens/clicks
      // We'll fetch events for the most recent 50 emails to get aggregate stats (sample-based)
      const recentSent = (sentNotifications || []).slice(0, 50);
      
      let totalOpens = 0;
      let totalClicks = 0;
      let totalBounced = 0;
      let totalComplained = 0;
      let sampledCount = 0;

      // Fetch events for each notification (in batches to avoid rate limits)
      const batchSize = 5; // Smaller batches to be more respectful of rate limits
      for (let i = 0; i < recentSent.length; i += batchSize) {
        const batch = recentSent.slice(i, i + batchSize);
        const eventPromises = batch.map(async (notification) => {
          if (!notification.mailgun_message_id) return null;
          try {
            const { data, error } = await supabase.functions.invoke('get-mailgun-events', {
              body: { messageId: notification.mailgun_message_id },
            });
            if (error || !data?.success) return null;
            sampledCount++;
            return data.summary;
          } catch (error) {
            console.error('Error fetching events for notification:', error);
            return null;
          }
        });

        const results = await Promise.all(eventPromises);
        results.forEach((summary) => {
          if (summary) {
            totalOpens += summary.opens || 0;
            totalClicks += summary.clicks || 0;
            totalBounced += summary.bounced || 0;
            totalComplained += summary.complained || 0;
          }
        });

        // Small delay to avoid rate limiting
        if (i + batchSize < recentSent.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Calculate rates based on sampled data
      // Note: Rates are calculated from the sample, not the full dataset
      const sampleSize = sampledCount || 1; // Avoid division by zero
      const openRate = sampleSize > 0 ? (totalOpens / sampleSize) * 100 : 0;
      const clickRate = sampleSize > 0 ? (totalClicks / sampleSize) * 100 : 0;
      const bounceRate = sampleSize > 0 ? (totalBounced / sampleSize) * 100 : 0;
      const complaintRate = sampleSize > 0 ? (totalComplained / sampleSize) * 100 : 0;

      setMetrics({
        ...counts,
        totalOpens,
        totalClicks,
        totalBounced,
        totalComplained,
        openRate: Math.round(openRate * 10) / 10, // Round to 1 decimal
        clickRate: Math.round(clickRate * 10) / 10,
        bounceRate: Math.round(bounceRate * 10) / 10,
        complaintRate: Math.round(complaintRate * 10) / 10,
      });
    } catch (error) {
      console.error("Error fetching metrics:", error);
      message.error("Failed to fetch email metrics");
    } finally {
      setMetricsLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    // Clean up any existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Create a new channel for booking_email_notifications
    const channel = supabase
      .channel('booking_email_notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'booking_email_notifications',
        },
        (payload: RealtimePostgresChangesPayload<any>) => {
          console.log('Realtime notification update:', payload);
          
            if (payload.eventType === 'INSERT') {
            // Add new notification to the top of the list
            setNotifications((prev) => {
              // Check if notification already exists (avoid duplicates)
              const exists = prev.some(n => n.id === payload.new.id);
              if (exists) return prev;
              
              // Apply current filters to the new notification
              const newNotification = payload.new as EmailNotification;
              if (shouldShowNotification(newNotification)) {
                return [newNotification, ...prev].slice(0, 500); // Keep max 500
              }
              return prev;
            });
            
            // Update metrics
            fetchMetrics();
            
            // Show a subtle notification
            message.info({
              content: `New email notification: ${payload.new.recipient}`,
              duration: 3,
            });
          } else if (payload.eventType === 'UPDATE') {
            // Update existing notification in the list
            setNotifications((prev) => {
              return prev.map((notification) => {
                if (notification.id === payload.new.id) {
                  return { ...notification, ...payload.new } as EmailNotification;
                }
                return notification;
              });
            });
            
            // Update metrics if status changed
            if (payload.new.status !== payload.old.status) {
              fetchMetrics();
            }
          } else if (payload.eventType === 'DELETE') {
            // Remove deleted notification
            setNotifications((prev) => {
              return prev.filter((notification) => notification.id !== payload.old.id);
            });
            
            // Update metrics
            fetchMetrics();
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Subscribed to booking_email_notifications realtime updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to realtime updates');
        } else if (status === 'TIMED_OUT') {
          console.warn('⏱️ Realtime subscription timed out');
        } else if (status === 'CLOSED') {
          console.log('🔌 Realtime subscription closed');
        }
      });

    channelRef.current = channel;
  };

  // Helper function to check if notification matches current filters
  const shouldShowNotification = (notification: EmailNotification): boolean => {
    if (filters.bookingId && notification.booking_id !== filters.bookingId) {
      return false;
    }
    if (filters.recipient && !notification.recipient.toLowerCase().includes(filters.recipient.toLowerCase())) {
      return false;
    }
    if (filters.templateId && notification.notification_type !== filters.templateId) {
      return false;
    }
    if (filters.status && notification.status !== filters.status) {
      return false;
    }
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const createdAt = dayjs(notification.created_at);
      const startDate = filters.dateRange[0].startOf("day");
      const endDate = filters.dateRange[1].endOf("day");
      if (createdAt.isBefore(startDate) || createdAt.isAfter(endDate)) {
        return false;
      }
    }
    return true;
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Query from the new booking_email_notifications table
      let query = supabase
        .from("booking_email_notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      // Apply filters
      if (filters.bookingId) {
        query = query.eq("booking_id", filters.bookingId);
      }
      if (filters.recipient) {
        query = query.ilike("recipient", `%${filters.recipient}%`);
      }
      if (filters.templateId) {
        // Filter by notification_type
        query = query.eq("notification_type", filters.templateId);
      }
      if (filters.status) {
        query = query.eq("status", filters.status);
      }
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        const startDate = filters.dateRange[0].startOf("day").toISOString();
        const endDate = filters.dateRange[1].endOf("day").toISOString();
        query = query.gte("created_at", startDate).lte("created_at", endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      console.error("Error fetching email notifications:", error);
      message.error("Failed to fetch email notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Re-subscribe to realtime when filters change to ensure we get all relevant updates
    // (The subscription itself doesn't need to change, but we re-fetch to apply filters)
  }, [filters]);

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "sent":
        return "green";
      case "failed":
        return "red";
      case "pending":
        return "orange";
      default:
        return "default";
    }
  };

  const getNotificationTypeLabel = (notificationType: string) => {
    const labels: Record<string, string> = {
      status_change: "Status Change",
      expected_delivery_date_change: "Expected delivery date",
      container_operational_eta_change: "Container ETA",
      container_etd_change: "Container ETD",
      container_eta_change: "Expected delivery date",
    };
    return labels[notificationType] || notificationType;
  };

  const fetchMailgunEvents = async (messageId: string) => {
    if (!messageId) {
      setMailgunEvents(null);
      return;
    }

    try {
      setMailgunLoading(true);
      const { data, error } = await supabase.functions.invoke('get-mailgun-events', {
        body: { messageId },
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setMailgunEvents(data);
      } else {
        throw new Error(data?.error || 'Failed to fetch Mailgun events');
      }
    } catch (error) {
      console.error('Error fetching Mailgun events:', error);
      message.error(
        error instanceof Error 
          ? `Failed to fetch Mailgun events: ${error.message}`
          : 'Failed to fetch Mailgun events'
      );
      setMailgunEvents(null);
    } finally {
      setMailgunLoading(false);
    }
  };

  const handleViewDetails = (notification: EmailNotification) => {
    setSelectedNotification(notification);
    setModalVisible(true);
    setMailgunEvents(null); // Reset Mailgun events
    
    // Fetch Mailgun events if message ID exists
    if (notification.mailgun_message_id) {
      fetchMailgunEvents(notification.mailgun_message_id);
    }
  };

  const handleTestEmail = async (values: {
    bookingId?: number;
    containerId?: number;
    emailType: string;
    testRecipient: string;
    testStatus?: string;
  }) => {
    // Convert string inputs to numbers if provided
    const bookingId = values.bookingId ? Number(values.bookingId) : undefined;
    const containerId = values.containerId ? Number(values.containerId) : undefined;
    try {
      setTestLoading(true);

      // Call the test email edge function
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          emailType: values.emailType,
          recipient: values.testRecipient,
          bookingId: bookingId,
          containerId: containerId,
          testStatus: values.testStatus,
        },
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        message.success(`Test email sent successfully to ${values.testRecipient}!`);
        console.log('Test email result:', data);
      } else {
        throw new Error(data?.error || 'Failed to send test email');
      }

      setTestModalVisible(false);
      testForm.resetFields();
      setSelectedTestStatus('');
      setTestStatusMessagePreview('');
    } catch (error) {
      console.error("Error sending test email:", error);
      message.error(
        error instanceof Error 
          ? `Failed to send test email: ${error.message}`
          : "Failed to send test email"
      );
    } finally {
      setTestLoading(false);
    }
  };

  // Helper function to convert HTML to plain text (for editing)
  const htmlToPlainText = (html: string): string => {
    if (!html) return '';
    // Create a temporary div to parse HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content and preserve line breaks
    return tmp.textContent || tmp.innerText || '';
  };

  // Helper function to convert plain text to HTML (for saving)
  const plainTextToHtml = (text: string): string => {
    if (!text || !text.trim()) return '';
    
    // Trim the input
    const trimmed = text.trim();
    
    // If there are no line breaks, just wrap in a single <p> tag
    if (!trimmed.includes('\n')) {
      return `<p>${trimmed}</p>`;
    }
    
    // Split by double line breaks (paragraphs) or single line breaks
    // First, normalize multiple line breaks to double line breaks
    const normalized = trimmed.replace(/\n{3,}/g, '\n\n');
    
    // Split by double line breaks to get paragraphs
    const paragraphs = normalized
      .split(/\n\s*\n/)
      .map(p => p.trim())
      .filter(p => p.length > 0)
      .map(p => {
        // Replace single line breaks within a paragraph with <br>
        return `<p>${p.replace(/\n/g, '<br>')}</p>`;
      });
    
    return paragraphs.join('');
  };

  // Fetch status messages
  const fetchStatusMessages = async () => {
    setStatusMessagesLoading(true);
    try {
      const { data, error } = await supabase
        .from('booking_status_email_messages')
        .select('*')
        .order('status');
      
      if (error) throw error;
      
      // Get all possible statuses
      const allStatuses = [
        'waiting_for_goods',
        'goods_received_origin',
        'in_transit',
        'customs_pending_clearance',
        'customs_cleared',
        'fyco',
        'delivery_planned',
        'out_for_delivery',
        'delivered',
        'added_to_stock',
        'cancelled',
      ];
      
      // Merge existing messages with all statuses
      const statusMap = new Map((data || []).map((msg: any) => [msg.status, msg]));
      const allMessages = allStatuses.map(status => ({
        status,
        message_html: statusMap.get(status)?.message_html || '',
        id: statusMap.get(status)?.id,
      }));
      
      setStatusMessages(allMessages);
    } catch (error) {
      console.error('Error fetching status messages:', error);
      message.error('Failed to load status messages');
    } finally {
      setStatusMessagesLoading(false);
    }
  };

  // Save status message (converts plain text to HTML)
  const saveStatusMessage = async (status: string, plainText: string) => {
    try {
      // Convert plain text to HTML
      const messageHtml = plainTextToHtml(plainText);
      
      const { error } = await supabase
        .from('booking_status_email_messages')
        .upsert({
          status,
          message_html: messageHtml,
        }, {
          onConflict: 'status',
        });
      
      if (error) throw error;
      
      message.success(`Status message for ${status} saved successfully`);
      fetchStatusMessages();
      setEditingStatus(null);
      statusMessageForm.resetFields();
    } catch (error) {
      console.error('Error saving status message:', error);
      message.error('Failed to save status message');
    }
  };

  const statusLabels: Record<string, string> = {
    waiting_for_goods: 'Wachten op Goederen',
    goods_received_origin: 'Goederen Ontvangen bij Oorsprong',
    in_transit: 'In Transit',
    customs_pending_clearance: 'Douane in Afwachting',
    customs_cleared: 'Douane Geklaard',
    fyco: 'FYCO',
    delivery_planned: 'Levering Gepland',
    out_for_delivery: 'Onderweg naar Bestemming',
    delivered: 'Afgeleverd',
    added_to_stock: 'Toegevoegd aan Voorraad',
    cancelled: 'Geannuleerd',
  };

  useEffect(() => {
    if (activeMainTab === 'status-messages') {
      fetchStatusMessages();
    }
  }, [activeMainTab]);

  const columns = [
    {
      title: "Booking",
      dataIndex: "booking_reference",
      key: "booking_reference",
      width: 120,
      ellipsis: true,
      render: (_: any, record: EmailNotification) => {
        const bookingRef = record.booking_reference || 'N/A';
        const transportType = record.transport_type || '';
        // Always append transport type if available (database stores them separately)
        if (transportType && bookingRef !== 'N/A') {
          return `${bookingRef}${transportType}`;
        }
        return bookingRef;
      },
    },
    {
      title: "Recipient",
      dataIndex: "recipient",
      key: "recipient",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "notification_type",
      key: "notification_type",
      width: 120,
      render: (notificationType: string) => {
        const label = getNotificationTypeLabel(notificationType);
        return label === "Status Change" ? "Status" : "ETA";
      },
    },
    {
      title: "Subject",
      dataIndex: "email_subject",
      key: "email_subject",
      ellipsis: true,
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 80,
      render: (status: string | null) => (
        <Tag color={getStatusColor(status)} style={{ margin: 0 }}>
          {status || "pending"}
        </Tag>
      ),
    },
    {
      title: "Sent",
      dataIndex: "sent_at",
      key: "sent_at",
      width: 140,
      render: (sentAt: string | null) =>
        sentAt ? dayjs(sentAt).format("DD-MM-YYYY HH:mm") : "-",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      render: (createdAt: string) =>
        dayjs(createdAt).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: any, record: EmailNotification) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          size="small"
          type="text"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <AdminNavBar />
      <div style={{ marginBottom: "24px" }}>
        <Title level={2}>Email Notifications</Title>
      </div>

      <Tabs activeKey={activeMainTab} onChange={setActiveMainTab}>
        <TabPane tab="Notifications" key="notifications">

      {/* Metrics Dashboard */}
      <Card 
        title={
          <Space size="small">
            <BarChartOutlined />
            <span>Email Metrics</span>
          </Space>
        }
        extra={
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchMetrics}
            loading={metricsLoading}
            size="small"
            type="text"
          />
        }
        bodyStyle={{ padding: "12px" }}
        style={{ marginBottom: "16px" }}
      >
        <Spin spinning={metricsLoading}>
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={6} md={3}>
              <div style={{ textAlign: "center", padding: "8px 4px" }}>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1890ff", lineHeight: 1.2 }}>
                  {metrics.total.toLocaleString()}
                </div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Total</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <div style={{ textAlign: "center", padding: "8px 4px" }}>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#52c41a", lineHeight: 1.2 }}>
                  {metrics.sent.toLocaleString()}
                </div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Sent</div>
                {metrics.sent > 0 && (
                  <div style={{ fontSize: "10px", color: "#999", marginTop: 1 }}>
                    {Math.round((metrics.sent / metrics.total) * 100)}%
                  </div>
                )}
              </div>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <div style={{ textAlign: "center", padding: "8px 4px" }}>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#faad14", lineHeight: 1.2 }}>
                  {metrics.pending.toLocaleString()}
                </div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Pending</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <div style={{ textAlign: "center", padding: "8px 4px" }}>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#ff4d4f", lineHeight: 1.2 }}>
                  {metrics.failed.toLocaleString()}
                </div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Failed</div>
                {metrics.failed > 0 && (
                  <div style={{ fontSize: "10px", color: "#999", marginTop: 1 }}>
                    {Math.round((metrics.failed / metrics.total) * 100)}%
                  </div>
                )}
              </div>
            </Col>
            {metrics.sent > 0 && (
              <>
                <Col xs={12} sm={6} md={3}>
                  <div style={{ textAlign: "center", padding: "8px 4px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1890ff", lineHeight: 1.2 }}>
                      {metrics.totalOpens.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Opens</div>
                    <div style={{ fontSize: "10px", color: "#52c41a", marginTop: 1, fontWeight: "500" }}>
                      {metrics.openRate}%
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <div style={{ textAlign: "center", padding: "8px 4px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#722ed1", lineHeight: 1.2 }}>
                      {metrics.totalClicks.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Clicks</div>
                    <div style={{ fontSize: "10px", color: "#722ed1", marginTop: 1, fontWeight: "500" }}>
                      {metrics.clickRate}%
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <div style={{ textAlign: "center", padding: "8px 4px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#ff9800", lineHeight: 1.2 }}>
                      {metrics.totalBounced.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Bounced</div>
                    {metrics.totalBounced > 0 && (
                      <div style={{ fontSize: "10px", color: "#ff9800", marginTop: 1, fontWeight: "500" }}>
                        {metrics.bounceRate}%
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <div style={{ textAlign: "center", padding: "8px 4px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#ff4d4f", lineHeight: 1.2 }}>
                      {metrics.totalComplained.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>Complaints</div>
                    {metrics.totalComplained > 0 && (
                      <div style={{ fontSize: "10px", color: "#ff4d4f", marginTop: 1, fontWeight: "500" }}>
                        {metrics.complaintRate}%
                      </div>
                    )}
                  </div>
                </Col>
              </>
            )}
          </Row>
          {metrics.sent > 0 && (
            <div style={{ marginTop: 8, fontSize: "10px", color: "#999", textAlign: "center" }}>
              * Metrics based on sample of recent 50 emails from new system only (excludes n8n emails)
            </div>
          )}
        </Spin>
      </Card>

      <Card bodyStyle={{ padding: "16px" }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {/* Filters */}
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Booking ID"
                type="number"
                value={filters.bookingId}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    bookingId: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Recipient Email"
                value={filters.recipient}
                onChange={(e) =>
                  setFilters({ ...filters, recipient: e.target.value || undefined })
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Email Type"
                style={{ width: "100%" }}
                value={filters.templateId}
                onChange={(value) =>
                  setFilters({ ...filters, templateId: value })
                }
                allowClear
              >
                <Option value="status_change">Status Change</Option>
                <Option value="expected_delivery_date_change">
                  Expected delivery date
                </Option>
                <Option value="container_operational_eta_change">
                  Container ETA
                </Option>
                <Option value="container_etd_change">Container ETD</Option>
                <Option value="container_eta_change">
                  Expected delivery date (legacy)
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Status"
                style={{ width: "100%" }}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
                allowClear
              >
                <Option value="sent">Sent</Option>
                <Option value="failed">Failed</Option>
                <Option value="pending">Pending</Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                style={{ width: "100%" }}
                value={filters.dateRange}
                onChange={(dates) =>
                  setFilters({
                    ...filters,
                    dateRange: dates as [Dayjs, Dayjs] | undefined,
                  })
                }
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchNotifications}
                loading={loading}
              >
                Refresh
              </Button>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={() => setTestModalVisible(true)}
              >
                Test Email
              </Button>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={notifications}
            loading={loading}
            rowKey="id"
            pagination={{ 
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} notifications`,
            }}
            scroll={{ x: 1000 }}
            size="small"
            bordered={false}
          />
        </Space>
      </Card>
        </TabPane>
        
        <TabPane tab="Status Messages" key="status-messages">
          <Card 
            title="Status-Specific Email Messages"
            extra={
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchStatusMessages}
                loading={statusMessagesLoading}
                size="small"
              >
                Refresh
              </Button>
            }
          >
            <Alert
              message="Custom Status Messages"
              description="Add custom text that will be inserted in status update emails. This text appears right after 'De status van je booking... is gewijzigd.' Enter plain text - it will be automatically converted to HTML. Leave empty to use default email format."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Spin spinning={statusMessagesLoading}>
              <Table
                dataSource={statusMessages}
                rowKey="status"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 200,
                    render: (status: string) => statusLabels[status] || status,
                  },
                  {
                    title: "Message",
                    dataIndex: "message_html",
                    key: "message_html",
                    render: (html: string, record: any) => {
                      if (editingStatus === record.status) {
                        // Convert HTML to plain text for editing
                        const plainText = htmlToPlainText(html);
                        return (
                          <Form
                            form={statusMessageForm}
                            onFinish={(values) => {
                              saveStatusMessage(record.status, values.message_text || '');
                            }}
                            initialValues={{ message_text: plainText }}
                          >
                            <Form.Item 
                              name="message_text"
                              help="Enter plain text. Line breaks will be converted to HTML automatically."
                            >
                              <TextArea
                                rows={6}
                                placeholder="Enter your message in plain text. Use line breaks to create paragraphs."
                                style={{ fontFamily: 'inherit' }}
                              />
                            </Form.Item>
                            <Space>
                              <Button type="primary" htmlType="submit" size="small">
                                Save
                              </Button>
                              <Button 
                                size="small"
                                onClick={() => {
                                  setEditingStatus(null);
                                  statusMessageForm.resetFields();
                                }}
                              >
                                Cancel
                              </Button>
                            </Space>
                          </Form>
                        );
                      }
                      return (
                        <div>
                          {html ? (
                            <div 
                              dangerouslySetInnerHTML={{ __html: html }}
                              style={{ 
                                marginBottom: 8,
                                padding: 8,
                                backgroundColor: '#f5f5f5',
                                borderRadius: 4,
                                fontSize: 12
                              }}
                            />
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>No custom message</Text>
                          )}
                          <Button
                            size="small"
                            onClick={() => {
                              setEditingStatus(record.status);
                              // Convert HTML to plain text for editing
                              const plainText = htmlToPlainText(html);
                              statusMessageForm.setFieldsValue({ message_text: plainText });
                            }}
                          >
                            {html ? 'Edit' : 'Add Message'}
                          </Button>
                        </div>
                      );
                    },
                  },
                ]}
              />
            </Spin>
          </Card>
        </TabPane>
      </Tabs>

      {/* View Details Modal */}
      <Modal
        title="Email Notification Details"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setMailgunEvents(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setModalVisible(false);
            setMailgunEvents(null);
          }}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        {selectedNotification && (
          <Tabs defaultActiveKey="details">
            <TabPane tab="Details" key="details">
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Booking Reference">
                    {(() => {
                      const bookingRef = selectedNotification.booking_reference || 'N/A';
                      const transportType = selectedNotification.transport_type || '';
                      // Always append transport type if available (database stores them separately)
                      if (transportType && bookingRef !== 'N/A') {
                        return `${bookingRef}${transportType}`;
                      }
                      return bookingRef;
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Recipient">
                    {selectedNotification.recipient}
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    {getNotificationTypeLabel(selectedNotification.notification_type)}
                  </Descriptions.Item>
                  {(selectedNotification.notification_type ===
                    "expected_delivery_date_change" ||
                    selectedNotification.notification_type ===
                      "container_operational_eta_change" ||
                    selectedNotification.notification_type ===
                      "container_etd_change" ||
                    selectedNotification.notification_type ===
                      "container_eta_change") && (
                    <>
                      <Descriptions.Item label="Container Reference">
                        {selectedNotification.container_reference || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          selectedNotification.notification_type ===
                          "container_etd_change"
                            ? "Old ETD"
                            : "Old ETA"
                        }
                      >
                        {selectedNotification.old_eta
                          ? dayjs(selectedNotification.old_eta).format("YYYY-MM-DD")
                          : "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          selectedNotification.notification_type ===
                          "container_etd_change"
                            ? "New ETD"
                            : "New ETA"
                        }
                      >
                        {selectedNotification.new_eta
                          ? dayjs(selectedNotification.new_eta).format("YYYY-MM-DD")
                          : "N/A"}
                      </Descriptions.Item>
                    </>
                  )}
                  {selectedNotification.notification_type === "status_change" && (
                    <>
                      <Descriptions.Item label="Old Status">
                        {selectedNotification.old_status || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="New Status">
                        {selectedNotification.new_status || "N/A"}
                      </Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedNotification.status)}>
                      {selectedNotification.status || "pending"}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Subject">
                    {selectedNotification.email_subject || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sent At">
                    {selectedNotification.sent_at
                      ? dayjs(selectedNotification.sent_at).format("DD-MM-YYYY HH:mm:ss")
                      : "Not sent"}
                  </Descriptions.Item>
                  {selectedNotification.error_message && (
                    <Descriptions.Item label="Error">
                      <Tag color="red">{selectedNotification.error_message}</Tag>
                    </Descriptions.Item>
                  )}
                  {selectedNotification.mailgun_message_id && (
                    <Descriptions.Item label="Mailgun Message ID">
                      <code>{selectedNotification.mailgun_message_id}</code>
                    </Descriptions.Item>
                  )}
                </Descriptions>
                {selectedNotification.email_body_html && (
                  <div>
                    <strong>Email Content (HTML):</strong>
                    <div
                      style={{
                        border: "1px solid #d9d9d9",
                        padding: "12px",
                        maxHeight: "400px",
                        overflow: "auto",
                        marginTop: "8px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedNotification.email_body_html,
                      }}
                    />
                  </div>
                )}
              </Space>
            </TabPane>
            <TabPane 
              tab={
                <span>
                  Events
                  {mailgunEvents && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {mailgunEvents.summary.total}
                    </Tag>
                  )}
                </span>
              } 
              key="mailgun"
            >
              {mailgunLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>Loading Mailgun events...</div>
                </div>
              ) : mailgunEvents ? (
                <Space direction="vertical" style={{ width: "100%" }} size="large">
                  {/* Info about multiple recipients */}
                  {mailgunEvents.summary.delivered > 1 && (
                    <Alert
                      message="Multiple Recipients"
                      description="This email was sent to multiple recipients (including BCC). Mailgun creates separate events for each recipient, which is why you see multiple 'delivered' and 'accepted' events."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {/* Summary Cards */}
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#52c41a" }}>
                            {mailgunEvents.summary.delivered}
                          </div>
                          <div>Delivered</div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1890ff" }}>
                            {mailgunEvents.summary.opens}
                          </div>
                          <div>Opens</div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#722ed1" }}>
                            {mailgunEvents.summary.clicks}
                          </div>
                          <div>Clicks</div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "24px", fontWeight: "bold", color: mailgunEvents.summary.failed > 0 ? "#ff4d4f" : "#52c41a" }}>
                            {mailgunEvents.summary.failed}
                          </div>
                          <div>Failed</div>
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {/* Alerts for issues */}
                  {mailgunEvents.summary.bounced > 0 && (
                    <Alert
                      message="Email Bounced"
                      description={`This email bounced ${mailgunEvents.summary.bounced} time(s).`}
                      type="warning"
                      icon={<WarningOutlined />}
                      showIcon
                    />
                  )}
                  {mailgunEvents.summary.complained > 0 && (
                    <Alert
                      message="Spam Complaint"
                      description={`This email received ${mailgunEvents.summary.complained} spam complaint(s).`}
                      type="error"
                      icon={<WarningOutlined />}
                      showIcon
                    />
                  )}

                  {/* Event Timeline - Grouped by Recipient */}
                  <div>
                    <Title level={4}>Event Timeline</Title>
                    {(() => {
                      // Group events by recipient
                      const eventsByRecipient: Record<string, MailgunEvent[]> = {};
                      mailgunEvents.events.forEach((event) => {
                        const recipient = event.recipient || event.envelope?.targets || 'Unknown';
                        if (!eventsByRecipient[recipient]) {
                          eventsByRecipient[recipient] = [];
                        }
                        eventsByRecipient[recipient].push(event);
                      });

                      // Get unique recipients
                      const recipients = Object.keys(eventsByRecipient);

                      return recipients.map((recipient) => {
                        const recipientEvents = eventsByRecipient[recipient].sort(
                          (a, b) => b.timestamp - a.timestamp
                        );

                        const getEventIcon = (eventType: string) => {
                          switch (eventType) {
                            case 'delivered':
                              return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                            case 'opened':
                              return <EyeOutlined style={{ color: '#1890ff' }} />;
                            case 'clicked':
                              return <LinkOutlined style={{ color: '#722ed1' }} />;
                            case 'failed':
                            case 'bounced':
                              return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
                            case 'complained':
                              return <WarningOutlined style={{ color: '#ff4d4f' }} />;
                            default:
                              return null;
                          }
                        };

                        const getEventColor = (eventType: string) => {
                          switch (eventType) {
                            case 'delivered':
                              return 'green';
                            case 'opened':
                              return 'blue';
                            case 'clicked':
                              return 'purple';
                            case 'failed':
                            case 'bounced':
                            case 'complained':
                              return 'red';
                            default:
                              return 'gray';
                          }
                        };

                        return (
                          <div key={recipient} style={{ marginBottom: 32 }}>
                            <div style={{ 
                              marginBottom: 16, 
                              paddingBottom: 8, 
                              borderBottom: '2px solid #e8e8e8' 
                            }}>
                              <Title level={5} style={{ margin: 0 }}>
                                {recipient}
                                {recipient === selectedNotification?.recipient && (
                                  <Tag color="blue" style={{ marginLeft: 8 }}>Primary Recipient</Tag>
                                )}
                                {recipient !== selectedNotification?.recipient && (
                                  <Tag color="default" style={{ marginLeft: 8 }}>BCC</Tag>
                                )}
                              </Title>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                                {recipientEvents.length} event{recipientEvents.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <Timeline>
                              {recipientEvents.map((event, index) => (
                                <Timeline.Item
                                  key={`${recipient}-${index}`}
                                  dot={getEventIcon(event.event)}
                                  color={getEventColor(event.event)}
                                >
                                  <div>
                                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                      {event.event.charAt(0).toUpperCase() + event.event.slice(1)}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '12px', marginBottom: 8 }}>
                                      {dayjs.unix(event.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                                    </div>
                                    {event['client-info'] && (
                                      <div style={{ fontSize: '12px', marginBottom: 4 }}>
                                        <strong>Client:</strong> {event['client-info']['client-name']} ({event['client-info']['device-type']})
                                      </div>
                                    )}
                                    {event['geolocation'] && (
                                      <div style={{ fontSize: '12px', marginBottom: 4 }}>
                                        <strong>Location:</strong> {event['geolocation'].city}, {event['geolocation'].region}, {event['geolocation'].country}
                                      </div>
                                    )}
                                    {event.ip && (
                                      <div style={{ fontSize: '12px', marginBottom: 4 }}>
                                        <strong>IP:</strong> {event.ip}
                                      </div>
                                    )}
                                    {event['url'] && (
                                      <div style={{ fontSize: '12px', marginBottom: 4 }}>
                                        <strong>URL:</strong> <a href={event['url']} target="_blank" rel="noopener noreferrer">{event['url']}</a>
                                      </div>
                                    )}
                                    {event.reason && (
                                      <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
                                        <strong>Reason:</strong> {event.reason}
                                      </div>
                                    )}
                                    {event.delivery && (
                                      <div style={{ fontSize: '12px' }}>
                                        <strong>SMTP Response:</strong> {event.delivery.smtp_response} (Code: {event.delivery.code})
                                      </div>
                                    )}
                                    {event['delivery-status'] && (
                                      <div style={{ fontSize: '12px' }}>
                                        <strong>Delivery Status:</strong> {event['delivery-status'].message} (Code: {event['delivery-status'].code})
                                      </div>
                                    )}
                                  </div>
                                </Timeline.Item>
                              ))}
                            </Timeline>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </Space>
              ) : selectedNotification.mailgun_message_id ? (
                <Alert
                  message="No Mailgun Events Found"
                  description="This email was sent but no events have been recorded yet. Events may take a few moments to appear."
                  type="info"
                />
              ) : (
                <Alert
                  message="No Mailgun Message ID"
                  description="This notification doesn't have a Mailgun message ID, so we cannot fetch tracking events."
                  type="warning"
                />
              )}
            </TabPane>
          </Tabs>
        )}
      </Modal>

      {/* Test Email Modal */}
      <Modal
        title="Send Test Email"
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          testForm.resetFields();
          setSelectedTestStatus('');
          setTestStatusMessagePreview('');
        }}
        footer={null}
        width={600}
      >
        <Form
          form={testForm}
          layout="vertical"
          onFinish={handleTestEmail}
        >
          <Form.Item
            name="emailType"
            label="Email Type"
            rules={[{ required: true, message: "Please select email type" }]}
          >
            <Select 
              placeholder="Select email type"
              onChange={(value) => {
                if (value !== 'status_change') {
                  setSelectedTestStatus('');
                  setTestStatusMessagePreview('');
                  testForm.setFieldsValue({ testStatus: undefined });
                }
              }}
            >
              <Option value="expected_delivery_date_change">
                Expected delivery date
              </Option>
              <Option value="container_operational_eta_change">
                Container ETA
              </Option>
              <Option value="container_etd_change">Container ETD</Option>
              <Option value="container_eta_change">
                Expected delivery date (legacy)
              </Option>
              <Option value="status_change">Status Change</Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.emailType !== currentValues.emailType}
          >
            {({ getFieldValue }) => {
              const emailType = getFieldValue('emailType');
              if (emailType === 'status_change') {
                return (
                  <>
                    <Form.Item
                      name="testStatus"
                      label="Status (for custom message preview)"
                      help="Select a status to preview and include its custom message in the test email"
                    >
                      <Select 
                        placeholder="Select status"
                        allowClear
                        onChange={async (value) => {
                          setSelectedTestStatus(value || '');
                          if (value) {
                            try {
                              const { data, error } = await supabase
                                .from('booking_status_email_messages')
                                .select('message_html')
                                .eq('status', value)
                                .maybeSingle();
                              
                              if (!error && data?.message_html) {
                                setTestStatusMessagePreview(data.message_html);
                              } else {
                                setTestStatusMessagePreview('');
                              }
                            } catch (error) {
                              console.error('Error fetching status message:', error);
                              setTestStatusMessagePreview('');
                            }
                          } else {
                            setTestStatusMessagePreview('');
                          }
                        }}
                      >
                        <Option value="waiting_for_goods">Wachten op Goederen</Option>
                        <Option value="goods_received_origin">Goederen Ontvangen bij Oorsprong</Option>
                        <Option value="in_transit">In Transit</Option>
                        <Option value="customs_pending_clearance">Douane in Afwachting</Option>
                        <Option value="customs_cleared">Douane Geklaard</Option>
                        <Option value="fyco">FYCO</Option>
                        <Option value="delivery_planned">Levering Gepland</Option>
                        <Option value="out_for_delivery">Onderweg naar Bestemming</Option>
                        <Option value="delivered">Afgeleverd</Option>
                        <Option value="added_to_stock">Toegevoegd aan Voorraad</Option>
                        <Option value="cancelled">Geannuleerd</Option>
                      </Select>
                    </Form.Item>
                    {testStatusMessagePreview && (
                      <Form.Item label="Custom Message Preview">
                        <Alert
                          message="This custom message will be included in the test email:"
                          type="info"
                          showIcon
                          style={{ marginBottom: 8 }}
                        />
                        <div 
                          dangerouslySetInnerHTML={{ __html: testStatusMessagePreview }}
                          style={{
                            padding: 12,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 4,
                            border: '1px solid #d9d9d9',
                            minHeight: 50,
                          }}
                        />
                      </Form.Item>
                    )}
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item
            name="bookingId"
            label="Booking ID (optional - for status change emails)"
            help="If provided, will use real booking data. Otherwise uses test data."
          >
            <Input type="number" placeholder="Enter booking ID" />
          </Form.Item>
          <Form.Item
            name="containerId"
            label="Container ID (optional - for container ETA emails)"
            help="If provided, will use real container data. Otherwise uses test data."
          >
            <Input type="number" placeholder="Enter container ID" />
          </Form.Item>
          <Form.Item
            name="testRecipient"
            label="Test Recipient Email"
            rules={[
              { required: true, message: "Please enter test recipient email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="test@example.com" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={testLoading}
              >
                Send Test Email
              </Button>
              <Button onClick={() => {
                setTestModalVisible(false);
                testForm.resetFields();
                setSelectedTestStatus('');
                setTestStatusMessagePreview('');
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailNotificationsAdmin;

